import React, { useState, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { FiAlertTriangle, FiX, FiTrash2, FiAlertCircle } from 'react-icons/fi';
import SafeIcon from '../common/SafeIcon';
import useModalAccessibility from '../hooks/useModalAccessibility';

/**
 * DeleteConfirmationModal - System-wide hard delete confirmation
 * 
 * Features:
 * - Requires user to type confirmation phrase
 * - Shows dependencies that will be affected
 * - Visual warnings with red styling
 * - Prevents accidental deletions
 * - Can block deletion entirely when blockingDependencies is true
 * 
 * @param {boolean} isOpen - Whether modal is visible
 * @param {function} onClose - Close modal callback
 * @param {function} onConfirm - Confirm delete callback
 * @param {string} itemType - Type of item being deleted (e.g., "Case", "Staff Member", "Workflow")
 * @param {string} itemName - Name/ID of item being deleted
 * @param {object} dependencies - Object containing dependency information
 * @param {boolean} loading - Whether delete operation is in progress
 * @param {boolean} blockingDependencies - If true, deletion is blocked when dependencies exist
 */
const DeleteConfirmationModal = ({
  isOpen,
  onClose,
  onConfirm,
  itemType = 'item',
  itemName = '',
  dependencies = {},
  loading = false,
  blockingDependencies = false
}) => {
  const [confirmationText, setConfirmationText] = useState('');
  const [error, setError] = useState('');
  const modalRef = useModalAccessibility(isOpen, onClose);

  // Reset state when modal opens/closes
  useEffect(() => {
    if (isOpen) {
      setConfirmationText('');
      setError('');
    }
  }, [isOpen]);

  // Calculate total affected items
  const totalAffected = Object.values(dependencies).reduce((sum, dep) => {
    if (typeof dep === 'number') return sum + dep;
    if (Array.isArray(dep)) return sum + dep.length;
    return sum;
  }, 0);

  const hasNoDependencies = totalAffected === 0;
  const isDeletionBlocked = blockingDependencies && !hasNoDependencies;

  // Determine what user needs to type
  const confirmationPhrase = itemName || 'DELETE';
  const isConfirmationValid = confirmationText.trim() === confirmationPhrase;

  const handleConfirm = () => {
    if (!isConfirmationValid) {
      setError(`You must type "${confirmationPhrase}" exactly to confirm deletion`);
      return;
    }

    onConfirm();
  };

  const handleKeyPress = (e) => {
    if (e.key === 'Enter' && isConfirmationValid && !loading) {
      handleConfirm();
    }
  };

  if (!isOpen) return null;

  return (
    <AnimatePresence>
      <div className="fixed inset-0 z-50 overflow-y-auto">
        {/* Backdrop */}
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0 }}
          className="fixed inset-0 bg-black bg-opacity-50 transition-opacity"
          onClick={onClose}
        />

        {/* Modal */}
        <div className="flex min-h-full items-center justify-center p-4">
          <motion.div
            ref={modalRef}
            role="dialog"
            aria-modal="true"
            initial={{ opacity: 0, scale: 0.95, y: 20 }}
            animate={{ opacity: 1, scale: 1, y: 0 }}
            exit={{ opacity: 0, scale: 0.95, y: 20 }}
            className="relative bg-white rounded-lg shadow-xl max-w-2xl w-full"
            onClick={(e) => e.stopPropagation()}
          >
            {/* Header */}
            <div className="bg-red-600 text-white px-6 py-4 rounded-t-lg">
              <div className="flex items-center justify-between">
                <div className="flex items-center space-x-3">
                  <SafeIcon icon={FiAlertTriangle} className="text-2xl" />
                  <h2 className="text-xl font-bold">Confirm Permanent Deletion</h2>
                </div>
                <button
                  onClick={onClose}
                  disabled={loading}
                  className="text-white hover:text-red-100 transition-colors disabled:opacity-50"
                >
                  <SafeIcon icon={FiX} className="text-2xl" />
                </button>
              </div>
            </div>

            {/* Content */}
            <div className="px-6 py-4">
              {/* Warning Message */}
              <div className={`mb-4 p-4 border-2 rounded-lg ${isDeletionBlocked
                ? 'bg-red-100 border-red-400'
                : 'bg-red-50 border-red-200'
                }`}>
                <div className="flex items-start space-x-3">
                  <SafeIcon icon={FiAlertCircle} className="text-red-600 text-xl mt-0.5 flex-shrink-0" />
                  <div>
                    <h3 className="font-semibold text-red-900 mb-1">
                      {isDeletionBlocked ? 'Deletion Blocked!' : 'Warning: This action cannot be undone!'}
                    </h3>
                    <p className="text-sm text-red-800">
                      {isDeletionBlocked ? (
                        <>
                          This {itemType.toLowerCase()} cannot be deleted because it has active dependencies.
                          You must resolve these dependencies before deletion is allowed.
                        </>
                      ) : (
                        <>
                          You are about to permanently delete this {itemType.toLowerCase()}.
                          {!hasNoDependencies && ' This will also affect related items listed below.'}
                        </>
                      )}
                    </p>
                  </div>
                </div>
              </div>

              {/* Item Being Deleted */}
              <div className="mb-4 p-4 bg-gray-50 border border-gray-200 rounded-lg">
                <h4 className="font-semibold text-gray-900 mb-2">Item to be deleted:</h4>
                <div className="flex items-center space-x-2">
                  <SafeIcon icon={FiTrash2} className="text-red-600" />
                  <span className="font-medium text-gray-900">{itemType}:</span>
                  <span className="text-gray-700">{itemName || 'Unnamed'}</span>
                </div>
              </div>

              {/* Dependencies */}
              {!hasNoDependencies && (
                <div className={`mb-4 p-4 border-2 rounded-lg ${isDeletionBlocked
                  ? 'bg-red-50 border-red-300'
                  : 'bg-amber-50 border-amber-200'
                  }`}>
                  <h4 className={`font-semibold mb-3 flex items-center space-x-2 ${isDeletionBlocked ? 'text-red-900' : 'text-amber-900'
                    }`}>
                    <SafeIcon icon={FiAlertTriangle} className={isDeletionBlocked ? 'text-red-600' : 'text-amber-600'} />
                    <span>
                      {isDeletionBlocked
                        ? 'Active dependencies preventing deletion:'
                        : 'The following related items will also be affected:'}
                    </span>
                  </h4>
                  <div className="space-y-2">
                    {Object.entries(dependencies).map(([key, value]) => {
                      if (!value || (typeof value === 'number' && value === 0) || (Array.isArray(value) && value.length === 0)) {
                        return null;
                      }

                      const count = typeof value === 'number' ? value : Array.isArray(value) ? value.length : 0;
                      const items = Array.isArray(value) ? value : [];

                      return (
                        <div key={key} className="text-sm">
                          <div className={`flex items-center space-x-2 ${isDeletionBlocked ? 'text-red-800' : 'text-amber-800'
                            }`}>
                            <span className="font-semibold">•</span>
                            <span className="font-medium">{count}</span>
                            <span>{key}</span>
                          </div>
                          {items.length > 0 && items.length <= 5 && (
                            <ul className="ml-6 mt-1 space-y-1">
                              {items.map((item, idx) => (
                                <li key={idx} className={`text-xs ${isDeletionBlocked ? 'text-red-700' : 'text-amber-700'
                                  }`}>
                                  - {typeof item === 'string' ? item : item.name || item.title || 'Unnamed'}
                                </li>
                              ))}
                            </ul>
                          )}
                          {items.length > 5 && (
                            <p className={`ml-6 mt-1 text-xs ${isDeletionBlocked ? 'text-red-700' : 'text-amber-700'
                              }`}>
                              (Showing first 5 of {items.length} items)
                            </p>
                          )}
                        </div>
                      );
                    })}
                  </div>
                </div>
              )}

              {/* Confirmation Input - Only show if deletion is not blocked */}
              {!isDeletionBlocked && (
                <>
                  <div className="mb-4">
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      To confirm deletion, type{' '}
                      <span className="font-bold text-red-600">"{confirmationPhrase}"</span> below:
                    </label>
                    <input
                      type="text"
                      value={confirmationText}
                      onChange={(e) => {
                        setConfirmationText(e.target.value);
                        setError('');
                      }}
                      onKeyPress={handleKeyPress}
                      disabled={loading}
                      placeholder={`Type "${confirmationPhrase}" to confirm`}
                      className={`w-full px-4 py-2 border-2 rounded-lg focus:outline-none focus:ring-2 transition-colors ${error
                        ? 'border-red-300 focus:border-red-500 focus:ring-red-200'
                        : isConfirmationValid
                          ? 'border-green-300 focus:border-green-500 focus:ring-green-200'
                          : 'border-gray-300 focus:border-blue-500 focus:ring-blue-200'
                        } disabled:bg-gray-100 disabled:cursor-not-allowed`}
                      autoFocus
                    />
                    {error && (
                      <p className="mt-2 text-sm text-red-600 flex items-center space-x-1">
                        <SafeIcon icon={FiAlertCircle} />
                        <span>{error}</span>
                      </p>
                    )}
                    {isConfirmationValid && (
                      <p className="mt-2 text-sm text-green-600 font-medium">
                        ✓ Confirmation text matches
                      </p>
                    )}
                  </div>

                  {/* Final Warning */}
                  <div className="mb-4 p-3 bg-red-100 border border-red-300 rounded text-sm text-red-900">
                    <strong>⚠️ Final Warning:</strong> This deletion is permanent and cannot be reversed.
                    All data will be lost forever.
                  </div>
                </>
              )}

              {/* Blocked Message */}
              {isDeletionBlocked && (
                <div className="mb-4 p-4 bg-red-100 border-2 border-red-400 rounded-lg">
                  <div className="flex items-start space-x-3">
                    <SafeIcon icon={FiAlertTriangle} className="text-red-600 text-xl flex-shrink-0" />
                    <div>
                      <h4 className="font-semibold text-red-900 mb-2">Cannot Delete</h4>
                      <p className="text-sm text-red-800 mb-2">
                        This {itemType.toLowerCase()} cannot be deleted because it has active dependencies listed above.
                      </p>
                      <p className="text-sm text-red-800 font-medium">
                        To delete this {itemType.toLowerCase()}, you must first:
                      </p>
                      <ul className="mt-2 ml-4 text-sm text-red-800 list-disc space-y-1">
                        <li>Resolve or reassign all active cases</li>
                        <li>Remove all dependencies</li>
                        <li>Then try deleting again</li>
                      </ul>
                    </div>
                  </div>
                </div>
              )}
            </div>

            {/* Footer */}
            <div className="bg-gray-50 px-6 py-4 rounded-b-lg flex justify-end space-x-3">
              <button
                onClick={onClose}
                disabled={loading}
                className="px-4 py-2 border border-gray-300 rounded-lg text-gray-700 hover:bg-gray-100 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
              >
                {isDeletionBlocked ? 'Close' : 'Cancel'}
              </button>
              {!isDeletionBlocked && (
                <button
                  onClick={handleConfirm}
                  disabled={!isConfirmationValid || loading}
                  className={`px-6 py-2 rounded-lg font-semibold transition-all flex items-center space-x-2 ${isConfirmationValid && !loading
                    ? 'bg-red-600 text-white hover:bg-red-700 shadow-lg hover:shadow-xl'
                    : 'bg-gray-300 text-gray-500 cursor-not-allowed'
                    }`}
                >
                  {loading ? (
                    <>
                      <svg className="animate-spin h-5 w-5 text-white" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                        <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                        <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                      </svg>
                      <span>Deleting...</span>
                    </>
                  ) : (
                    <>
                      <SafeIcon icon={FiTrash2} />
                      <span>Permanently Delete</span>
                    </>
                  )}
                </button>
              )}
            </div>
          </motion.div>
        </div>
      </div>
    </AnimatePresence>
  );
};

export default DeleteConfirmationModal;
